import { Box, Typography } from '@mui/material';
import { Form, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import { FormikColorPicker } from 'shared/ui/ColorPicker';
import FormikInput from 'shared/ui/Formik/FormikInput/FormikInput';

export interface CategoryItemFormValues {
    color: string;
    title: string;
    glCode: string;
}

interface CatalogItemInputProps {
    formTitle: string;
    selectionSubtitle?: string;
    loading?: boolean;
    formikProps: FormikProps<CategoryItemFormValues>;
}

export const CatalogItemInput = ({
    formTitle,
    selectionSubtitle,
    loading,
    formikProps,
}: CatalogItemInputProps) => {
    const { t } = useTranslation();

    const colorError = () => {
        if (formikProps.errors.title) return null;
        if (formikProps.errors.color && formikProps.touched.color)
            return formikProps.errors.color;
    };

    return (
        <Form>
            <Typography
                data-testid="Popup-title"
                marginBottom={selectionSubtitle ? '16px' : '24px'}
                variant="subtitle2"
            >
                {formTitle}
            </Typography>
            {selectionSubtitle ? (
                <Typography
                    data-testid="category-form-selection-subtitle"
                    variant="body2"
                    marginBottom="36px"
                    sx={{ color: '#363636' }}
                >
                    {selectionSubtitle}
                </Typography>
            ) : null}
            <Box display="flex" flexDirection="column" gap="20px">
                <FormikInput
                    name="title"
                    label={t('Category Name')}
                    loading={loading}
                    minimized
                />
                <Box display="flex" gap="8px">
                    <Box flexBasis="50%">
                        <FormikInput
                            name="glCode"
                            label={t('GL Code (optional)')}
                            loading={loading}
                            minimized
                        />
                    </Box>

                    <Box display="flex" gap="16px">
                        <Box position="relative" flex="1 1">
                            <FormikInput
                                name="color"
                                hasError={Boolean(formikProps.errors?.color)}
                                label={t('Color')}
                                loading={loading}
                                minimized
                                disabled
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    document
                                        .getElementById('ColorPickerBox')
                                        ?.click();
                                }}
                                onBlurAction={() =>
                                    formikProps.setFieldTouched('color')
                                }
                                styleOverride={{
                                    // This is a workaround to prevent the color picker input from looking disabled
                                    '& .MuiInputBase-root.Mui-disabled': {
                                        cursor: 'pointer',
                                        input: {
                                            pointerEvents: 'none',
                                            background: 'none',
                                        },
                                        'fieldset.MuiOutlinedInput-notchedOutline':
                                            {
                                                borderColor:
                                                    'rgba(0, 0, 0, 0.23)',
                                            },
                                        '&:hover': {
                                            'fieldset.MuiOutlinedInput-notchedOutline':
                                                {
                                                    borderColor:
                                                        'rgba(0, 0, 0, 0.87)',
                                                },
                                        },
                                        '&.Mui-error': {
                                            'fieldset.MuiOutlinedInput-notchedOutline':
                                                {
                                                    borderColor: '#BF1B28',
                                                },
                                        },
                                    },
                                }}
                            />
                            <Box
                                position="absolute"
                                zIndex="1"
                                top="10px"
                                right="8px"
                                sx={{
                                    cursor: 'pointer',
                                }}
                            >
                                <FormikColorPicker name="color" />
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: '1.2rem',
                                    color: 'error.main',
                                    position: 'absolute',
                                    bottom: '0',
                                    transform: 'translate(0%, 100%)',
                                    ml: '0',
                                }}
                                position="absolute"
                            >
                                {colorError()}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Form>
    );
};
